import { MouseEvent, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import EventIcon from '@mui/icons-material/Event';
import { IconButton, Popover, Tooltip } from '@mui/material';
import { useSelector } from 'react-redux';
import { greys, mainColors } from '../../../../styling/theme';
import {
  percentageToTwoDecimalPlacesNoHundred,
  toTwoDecimalPlaces,
} from '../../../../utilities/numberFormatters';
import {
  getAvailableDateXMonthsAgo,
  getMostRecentAvailableDate,
  getMostRecentWeekday,
  getWeekdayXMonthsAgo,
} from '../../../../utilities/dateUtilities';
import { availableDatesSelector } from '../../../../redux/pages/selectors';
import InTableTimeSeries from '../../../charts/InTableTimeSeries';
import NoTimeSeries from '../../../charts/NoTimeSeries';
import RaptorLoading from '../../../feedback/RaptorLoading';
import RaptorDatePicker from '../../../selects/RaptorDatePicker.component';
import { useTimeSeriesData } from '../../../../api/rs-hg/risk/hooks/queries';
import type {
  RuleTimeSeriesPoint,
  TimeSeriesDateRange as DateRange,
  TimeSeriesKey,
  TimeSeriesParamsByKey,
  TimeSeriesRuleParams,
} from '../../../../api/rs-hg/risk/ruleTimeSeries.types';

type ChartPoint = {
  date: string;
  value: number | null;
  limit: number | null;
  valueDisplay?: string | null;
  limitDisplay?: string | null;
};

export type RuleTimeSeriesCellProps<K extends TimeSeriesKey> = {
  timeSeriesKey: K;
  ruleParams: TimeSeriesRuleParams<K>;
  chartId: string;
  enabled?: boolean;
  emptyMessage?: string;
  emptyTooltip?: string;
  /**
   * Seed from the page payload. When an array is passed (even empty), the cell
   * does not fetch until the user applies a range that differs from the seed.
   * Omit this prop to fetch on first load.
   */
  initialPoints?: RuleTimeSeriesPoint[] | null;
  /** Tooltip / tick label. Law and CSSF store fractions and need ×100. */
  formatter?: (value: number) => string;
};

function stopRowToggle(event: MouseEvent) {
  event.stopPropagation();
}

function parseNumeric(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const n = parseFloat(value);
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

function chartLimit(limit: RuleTimeSeriesPoint['limit']): number | null {
  if (Array.isArray(limit)) {
    return parseNumeric(limit[0]);
  }
  return parseNumeric(limit);
}

export function toChartPoints(
  points: RuleTimeSeriesPoint[] | undefined,
): ChartPoint[] {
  return (
    points?.map((point) => ({
      date: point.position_date,
      value: parseNumeric(point.value),
      limit: chartLimit(point.limit),
      valueDisplay: point.value_display ?? null,
      limitDisplay: point.limit_display ?? null,
    })) ?? []
  );
}

/** When points carry display labels, put one y tick on each labelled value. */
function displayLabelAxis(chartData: ChartPoint[]) {
  const labels = new Map<number, string>();
  chartData.forEach((point) => {
    if (point.value != null && point.valueDisplay) {
      labels.set(point.value, point.valueDisplay);
    }
    if (point.limit != null && point.limitDisplay) {
      labels.set(point.limit, point.limitDisplay);
    }
  });
  if (!labels.size) return null;

  const ticks = [...labels.keys()].sort((a, b) => a - b);
  const pad = (ticks[ticks.length - 1] - ticks[0]) * 0.15 || 1;
  return {
    yAxisTicks: ticks,
    yAxisTickFormatter: (value: number) =>
      labels.get(value) ?? toTwoDecimalPlaces(value),
    yAxisDomain: [ticks[0] - pad, ticks[ticks.length - 1] + pad],
  };
}

function getEmbeddedDateRange(
  points: RuleTimeSeriesPoint[] | null | undefined,
): DateRange | null {
  if (!points?.length) return null;
  const dates = points
    .map((point) => point.position_date)
    .filter(Boolean)
    .sort();
  if (!dates.length) return null;
  return { startDate: dates[0], endDate: dates[dates.length - 1] };
}

function RuleTimeSeriesCell<K extends TimeSeriesKey>({
  timeSeriesKey,
  ruleParams,
  chartId,
  enabled = true,
  emptyMessage = 'No time series data available',
  emptyTooltip = 'No time series data available for this rule.',
  initialPoints,
  formatter = percentageToTwoDecimalPlacesNoHundred,
}: RuleTimeSeriesCellProps<K>) {
  const hasEmbeddedSeed = Array.isArray(initialPoints);
  const datesToInclude = useSelector(availableDatesSelector) ?? [];
  const availableDateSet = useMemo(
    () => new Set(datesToInclude),
    [datesToInclude],
  );

  const initialRange = useMemo(
    () => getEmbeddedDateRange(initialPoints),
    [initialPoints],
  );

  const fallbackStartDate = datesToInclude.length
    ? getAvailableDateXMonthsAgo(1, datesToInclude)
    : getWeekdayXMonthsAgo(1);
  const fallbackEndDate = datesToInclude.length
    ? getMostRecentAvailableDate(datesToInclude)
    : getMostRecentWeekday();

  const clampToAvailable = (date: string, fallback: string) =>
    datesToInclude.length === 0 || availableDateSet.has(date)
      ? date
      : fallback;

  const [startDate, setStartDate] = useState(() =>
    clampToAvailable(
      initialRange?.startDate ?? fallbackStartDate,
      fallbackStartDate,
    ),
  );
  const [endDate, setEndDate] = useState(() =>
    clampToAvailable(
      initialRange?.endDate ?? fallbackEndDate,
      fallbackEndDate,
    ),
  );
  const [appliedRange, setAppliedRange] = useState<DateRange | null>(() =>
    hasEmbeddedSeed
      ? null
      : { startDate: fallbackStartDate, endDate: fallbackEndDate },
  );
  const [calendarAnchor, setCalendarAnchor] = useState<HTMLElement | null>(
    null,
  );

  useEffect(() => {
    if (!datesToInclude.length) return;
    setStartDate((prev) =>
      availableDateSet.has(prev) ? prev : fallbackStartDate,
    );
    setEndDate((prev) => (availableDateSet.has(prev) ? prev : fallbackEndDate));
  }, [
    availableDateSet,
    datesToInclude.length,
    fallbackEndDate,
    fallbackStartDate,
  ]);

  const baselineRange = initialRange ?? {
    startDate: fallbackStartDate,
    endDate: fallbackEndDate,
  };
  const currentAppliedRange = appliedRange ?? baselineRange;
  const rangeDiffersFromBaseline =
    !!appliedRange &&
    (appliedRange.startDate !== baselineRange.startDate ||
      appliedRange.endDate !== baselineRange.endDate);
  const shouldFetch =
    enabled &&
    !!appliedRange &&
    (!hasEmbeddedSeed || rangeDiffersFromBaseline);

  const rangeChangedFromApplied =
    startDate !== currentAppliedRange.startDate ||
    endDate !== currentAppliedRange.endDate;

  const params =
    shouldFetch && appliedRange
      ? ({ ...ruleParams, ...appliedRange } as TimeSeriesParamsByKey[K])
      : null;

  const { data, isLoading, isPlaceholderData, isError, isFetching, refetch } =
    useTimeSeriesData(timeSeriesKey, params);

  const canRetryFailedFetch = isError && !rangeChangedFromApplied;

  const canApply =
    enabled &&
    !!startDate &&
    !!endDate &&
    startDate <= endDate &&
    (rangeChangedFromApplied || canRetryFailedFetch) &&
    (datesToInclude.length === 0 ||
      (availableDateSet.has(startDate) && availableDateSet.has(endDate)));

  const seededChartData = useMemo(
    () => toChartPoints(initialPoints ?? undefined),
    [initialPoints],
  );
  const fetchedChartData = useMemo(
    () => toChartPoints(data?.time_series),
    [data],
  );

  const resetDraftDatesToApplied = () => {
    setStartDate(currentAppliedRange.startDate);
    setEndDate(currentAppliedRange.endDate);
  };

  const handleOpenCalendar = (event: MouseEvent<HTMLElement>) => {
    stopRowToggle(event);
    resetDraftDatesToApplied();
    setCalendarAnchor(event.currentTarget);
  };

  const handleCloseCalendar = () => {
    resetDraftDatesToApplied();
    setCalendarAnchor(null);
  };

  const handleApply = (event: MouseEvent) => {
    stopRowToggle(event);
    if (!canApply) return;
    if (canRetryFailedFetch) {
      refetch();
    } else {
      setAppliedRange({ startDate, endDate });
    }
    setCalendarAnchor(null);
  };

  const dateControls = (
    <Tooltip title={`Date range: ${startDate} → ${endDate}`}>
      <IconButton
        type="button"
        size="small"
        aria-label="Select custom date range"
        onClick={handleOpenCalendar}
      >
        <EventIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  const calendarPopover = (
    <Popover
      open={!!calendarAnchor}
      anchorEl={calendarAnchor}
      onClose={handleCloseCalendar}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{
        onClick: stopRowToggle,
      }}
    >
      <div className="flex flex-col gap-3 p-3">
        <div className="text-[12px] font-semibold text-gray-700">
          Custom date range
        </div>
        <div className="flex flex-row flex-wrap items-center gap-2">
          <RaptorDatePicker
            title="From:"
            selectedDate={dayjs(startDate)}
            datesToInclude={datesToInclude}
            tiedToActiveDate={false}
            mainRootStyle={{ backgroundColor: greys.grey100 }}
            handler={(val) => {
              if (!val) return;
              setStartDate(dayjs(val).format('YYYY-MM-DD'));
            }}
          />
          <RaptorDatePicker
            title="To:"
            selectedDate={dayjs(endDate)}
            datesToInclude={datesToInclude}
            tiedToActiveDate={false}
            mainRootStyle={{ backgroundColor: greys.grey100 }}
            handler={(val) => {
              if (!val) return;
              setEndDate(dayjs(val).format('YYYY-MM-DD'));
            }}
          />
        </div>
        <div className="flex items-center justify-end gap-1.5">
          <button
            type="button"
            className="rounded border border-gray-300 bg-white px-2 py-1 text-[11px] font-semibold text-gray-700 hover:bg-gray-50"
            onClick={(event) => {
              stopRowToggle(event);
              handleCloseCalendar();
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rounded bg-mainblue px-2 py-1 text-[11px] font-semibold text-white hover:bg-mainblue_hover disabled:cursor-not-allowed disabled:opacity-45"
            disabled={!canApply}
            onClick={handleApply}
          >
            Apply
          </button>
        </div>
      </div>
    </Popover>
  );

  const renderChartShell = (
    chartData: ChartPoint[],
    options?: {
      showLoadingOverlay?: boolean;
      errorOverlay?: string;
    },
  ) => {
    const shellData =
      chartData.length > 0
        ? chartData
        : [{ date: startDate, value: null, limit: null }];
    const labelAxis = displayLabelAxis(chartData);

    return (
      <div className="relative w-full">
        <InTableTimeSeries
          id={chartId}
          export
          headerActions={dateControls}
          lines={[
            { dataKey: 'limit', color: mainColors.Fail_darker },
            { dataKey: 'value', color: mainColors.mainBlue },
          ]}
          error={null}
          title=""
          data={shellData}
          yAxisTicks={labelAxis?.yAxisTicks}
          yAxisTickFormatter={labelAxis?.yAxisTickFormatter}
          yAxisDomain={labelAxis?.yAxisDomain}
          formatter={(
            value: number,
            name: string,
            item?: { payload?: ChartPoint },
          ) =>
            (name === 'value'
              ? item?.payload?.valueDisplay
              : item?.payload?.limitDisplay) ?? formatter(value)
          }
        />
        {options?.showLoadingOverlay ? (
          <div className="pointer-events-none absolute inset-0 z-[1] flex items-center justify-center bg-white/70">
            <RaptorLoading
              messages={['Loading time series…']}
              noMargin
              setWidth="2.5rem"
            />
          </div>
        ) : null}
        {options?.errorOverlay ? (
          <div className="pointer-events-none absolute inset-0 z-[1] flex items-center justify-center px-3 text-center">
            <p className="text-[12px] font-semibold text-red-700">
              {options.errorOverlay}
            </p>
          </div>
        ) : null}
      </div>
    );
  };

  const renderFetchedFallbackShell = (options: {
    showLoadingOverlay?: boolean;
    errorOverlay?: string;
  }) => {
    if (fetchedChartData.length) {
      return renderChartShell(fetchedChartData, options);
    }
    if (seededChartData.length) {
      return renderChartShell(seededChartData, options);
    }
    return renderChartShell([], options);
  };

  const renderChart = () => {
    if (!enabled) {
      return (
        <NoTimeSeries
          rowDisabled
          customMessage={emptyMessage}
          tooltipMessage={emptyTooltip}
        />
      );
    }

    if (isLoading || isPlaceholderData || (isFetching && isError)) {
      return renderFetchedFallbackShell({ showLoadingOverlay: true });
    }

    if (!shouldFetch) {
      if (!seededChartData.length) {
        return (
          <div className="flex flex-col items-end gap-1">
            {dateControls}
            <NoTimeSeries
              customMessage={emptyMessage}
              tooltipMessage={emptyTooltip}
            />
          </div>
        );
      }
      return renderChartShell(seededChartData);
    }

    if (isError) {
      return renderFetchedFallbackShell({
        errorOverlay:
          'Failed to load time series. Choose a date range and apply to try again.',
      });
    }

    if (!fetchedChartData.length) {
      return renderFetchedFallbackShell({
        errorOverlay: emptyMessage,
      });
    }

    return renderChartShell(fetchedChartData);
  };

  return (
    <div
      className="flex w-full flex-col px-2 py-1"
      onClick={stopRowToggle}
      onMouseDown={stopRowToggle}
    >
      {calendarPopover}
      {renderChart()}
    </div>
  );
}

export default RuleTimeSeriesCell;
